import { useCallback, useMemo } from "react";

import { styled } from "@linaria/react";
import { CheckIcon } from "@phosphor-icons/react";
import { match } from "ts-pattern";

import Button, { ButtonSize } from "@gx/dls/buttons/Button";
import FlexItem from "@gx/dls/containers/FlexItem";
import FlexWrapper, { AlignItems, FlexDirection } from "@gx/dls/containers/FlexWrapper";
import HorizontalDivider from "@gx/dls/dividers/HorizontalDivider";
import HelpIcon from "@gx/dls/icons/HelpIcon";
import RequiredMarker from "@gx/dls/text/RequiredMarker";
import Text, { TextVariant } from "@gx/dls/text/Text";
import { withTheme, type PropsWithTheme } from "@gx/dls/theme";
import Tooltip, { TooltipPosition } from "@gx/dls/tooltip/Tooltip";
import { SourceType } from "@gx/ts-contracts/services/database/models/public_pb";

import CreateSourceModalHeader from "@/pages/sources/components/create/components/CreateSourceModalHeader";
import CreateSourceModalWrapper from "@/pages/sources/components/create/components/CreateSourceModalWrapper";
import CreateSourceNameInput from "@/pages/sources/components/create/components/CreateSourceNameInput";
import CreateSourceProgressFooter from "@/pages/sources/components/create/components/CreateSourceProgressFooter";
import { CreateSourceFileUploadActionType } from "@/pages/sources/components/create/sources/file-upload/actions";
import {
  CREATE_SOURCE_FILE_UPLOAD_PHASE_TO_PROGRESS_BAR_VARIANT_MAP,
  SOURCE_FILE_UPLOAD_ACCEPTED_FILE_TYPES,
} from "@/pages/sources/components/create/sources/file-upload/constants";
import CreateSourceFileUploadProvider from "@/pages/sources/components/create/sources/file-upload/CreateSourceFileUploadProvider";
import { useCreateSourceFileUpload } from "@/pages/sources/components/create/sources/file-upload/hooks";
import { CreateSourceFileUploadPhase } from "@/pages/sources/components/create/sources/file-upload/types";
import { CreateSourceStep } from "@/pages/sources/components/create/types";
import DropzoneWidget from "@/pages/sources/components/dropzone/DropzoneWidget";

import { NOOP } from "@/utils/helpers";

const BodyWrapper = withTheme(styled.div<PropsWithTheme>`
  flex: 1;
  min-height: 0;
  width: 100%;
  overflow-y: auto;
  background-color: ${({ theme }) => theme.color.background.base};
  padding: 16px;
`);

interface CreateSourceFileUploadProps {
  onClose: () => void;
  onBack: () => void;
}

const CreateSourceFileUploadContent = ({ onClose, onBack }: CreateSourceFileUploadProps) => {
  const { state, dispatch, startUpload, isDisabled, nameError, fileProgressMap } =
    useCreateSourceFileUpload({ onSuccess: () => onClose() });

  const handleSourceNameChange = useCallback(
    (name: string) => {
      dispatch({ type: CreateSourceFileUploadActionType.SET_SOURCE_NAME, payload: name });
    },
    [dispatch],
  );

  const handleDrop = useCallback(
    (acceptedFiles: File[]) => {
      dispatch({ type: CreateSourceFileUploadActionType.ADD_FILES, payload: acceptedFiles });
    },
    [dispatch],
  );

  const handleFilesChange = useCallback(
    (newFiles: File[]) => {
      const currentNames = new Set(newFiles.map((f) => f.name));
      state.files.forEach((file, index) => {
        if (!currentNames.has(file.name)) {
          dispatch({ type: CreateSourceFileUploadActionType.REMOVE_FILE, payload: index });
        }
      });
    },
    [state.files, dispatch],
  );

  const handleStartUpload = useCallback(() => {
    void startUpload();
  }, [startUpload]);

  const renderButton = useMemo(() => {
    return match(state.phase)
      .with(CreateSourceFileUploadPhase.CREATING_SOURCE, () => (
        <Button
          label="Creating..."
          size={ButtonSize.LARGE}
          onClick={NOOP}
          isLoading={true}
          isDisabled={true}
          isIconTrailing
          fillWidth
        />
      ))
      .with(CreateSourceFileUploadPhase.UPLOADING, () => (
        <Button
          label="Uploading..."
          size={ButtonSize.LARGE}
          onClick={NOOP}
          isLoading={true}
          isDisabled={true}
          isIconTrailing
          fillWidth
        />
      ))
      .with(CreateSourceFileUploadPhase.COMPLETED, () => (
        <Button
          label="Done"
          icon={CheckIcon}
          size={ButtonSize.LARGE}
          onClick={NOOP}
          isLoading={false}
          isDisabled={true}
          isIconTrailing
          fillWidth
        />
      ))
      .otherwise(() => (
        <Button
          label="Upload"
          size={ButtonSize.LARGE}
          onClick={handleStartUpload}
          isLoading={false}
          isDisabled={state.files.length === 0}
          isIconTrailing
          fillWidth
        />
      ));
  }, [state.phase, state.files.length, handleStartUpload]);

  return (
    <CreateSourceModalWrapper step={CreateSourceStep.CONFIGURE}>
      <CreateSourceModalHeader
        sourceType={SourceType.FILE_UPLOAD}
        onClose={onClose}
        onBack={onBack}
      />
      <FlexItem>
        <HorizontalDivider />
      </FlexItem>

      <BodyWrapper>
        <FlexWrapper direction={FlexDirection.COLUMN} gap={16} fillWidth>
          <CreateSourceNameInput
            value={state.sourceName}
            onChange={handleSourceNameChange}
            error={nameError}
            isDisabled={isDisabled}
          />

          <FlexWrapper direction={FlexDirection.COLUMN} gap={8} fillWidth>
            <FlexWrapper alignItems={AlignItems.CENTER} gap={4}>
              <Text variant={TextVariant.SECONDARY}>
                Add files
                <RequiredMarker />
              </Text>
              <Tooltip
                body="Add files to the source. You can add multiple files at once."
                position={TooltipPosition.RIGHT}
              >
                <HelpIcon />
              </Tooltip>
            </FlexWrapper>
            <DropzoneWidget
              files={state.files}
              onFilesChange={handleFilesChange}
              onDrop={handleDrop}
              accept={SOURCE_FILE_UPLOAD_ACCEPTED_FILE_TYPES}
              fileProgressMap={fileProgressMap}
              isDisabled={isDisabled}
            />
          </FlexWrapper>
        </FlexWrapper>
      </BodyWrapper>

      <CreateSourceProgressFooter
        percentage={state.overallProgress}
        variant={CREATE_SOURCE_FILE_UPLOAD_PHASE_TO_PROGRESS_BAR_VARIANT_MAP[state.phase]}
      >
        {renderButton}
      </CreateSourceProgressFooter>
    </CreateSourceModalWrapper>
  );
};

const CreateSourceFileUpload = (props: CreateSourceFileUploadProps) => (
  <CreateSourceFileUploadProvider>
    <CreateSourceFileUploadContent {...props} />
  </CreateSourceFileUploadProvider>
);

export default CreateSourceFileUpload;
